import { DEFAULT_CONSTRAINT_NAME } from '@repo/commons/constants'
import { generateId } from '@repo/commons/utils'

import {
	Modality,
	SupportedLanguage,
	type Constraint,
	type OutputObject
} from '@semanticedge/persistence-service-noheaders-client/src'

type CSV = string | `${string},${string}`
export type BracketedCSV = `[${CSV}]`
export type ConstraintSetIds = BracketedCSV
export type OutputObjectPrompts = Record<
	ConstraintSetIds,
	Record<Modality, Record<SupportedLanguage, string[]>>
>

export type PersistenceOutputObjectPrompts =
	| { [key: string]: { [key: string]: string[] } }
	| undefined

export const makeEmptyPromptsForConstraintSet = (
	constraintSetIds: ConstraintSetIds,
	modalities: Modality[],
	supportedLanguages: SupportedLanguage[]
): OutputObjectPrompts => {
	// constraintKey => (modality => (language => prompts[]))
	const prompts: OutputObjectPrompts = {} as OutputObjectPrompts

	// create the outer bucket for this constraint set ids
	prompts[constraintSetIds] = {} as Record<Modality, Record<SupportedLanguage, string[]>>

	// for each modality, fill in each language with a single empty prompt
	modalities.forEach((modality) => {
		prompts[constraintSetIds][modality] = {} as Record<SupportedLanguage, string[]>
		supportedLanguages.forEach((lang) => {
			prompts[constraintSetIds][modality][lang] = [''] as string[]
		})
	})

	return prompts
}

export const getConstraintNamesFromKey = (constraints: Constraint[], key: string): string[] => {
	// Key is in the format '[id1, id2, ...]'
	const idsString = key.slice(1, -1) // remove the square brackets
	const ids = idsString.split(/,\s*/)

	const constraintNames = ids.map((id) => {
		const constraint = constraints.find((c) => c.id === id.trim())
		return constraint!.name!
	})

	return constraintNames
}

export const buildOutputObjectDefaults = (
	constraints: Constraint[],
	supportedModalities: Modality[],
	supportedLanguages: SupportedLanguage[]
): Partial<OutputObject> => {
	const defaultConstraintId: string | undefined = constraints.find(
		({ name }) => name === DEFAULT_CONSTRAINT_NAME
	)?.id

	if (!defaultConstraintId) {
		return {
			id: generateId()
		}
	}

	const constraintSetAsStr: ConstraintSetIds = `[${defaultConstraintId}]`

	const defaultPrompts: OutputObjectPrompts = makeEmptyPromptsForConstraintSet(
		constraintSetAsStr,
		supportedModalities,
		supportedLanguages
	)

	return {
		name: '',
		id: generateId(),
		prompts: defaultPrompts,
		orderedConstraintSetIds: [[defaultConstraintId]]
	}
}
